import axios from 'axios';
import { notifyUnauthorized } from './authEvents';

// NDAs, MSAs and other paper kept per company, and the suggestions the legal drive and the eSignature audit log
// make (app/endpoints/document_endpoints.py)
const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || '',
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' }
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) notifyUnauthorized();
    return Promise.reject(error);
  }
);

const call = async (request, fallbackMessage) => {
  try {
    return (await request()).data;
  } catch (error) {
    const detail = error.response?.data?.detail;
    throw new Error(typeof detail === 'string' ? detail : fallbackMessage);
  }
};

const documentsApi = {
  forCompany: (companyId) => call(
    () => apiClient.get(`/api/v2/companies/${companyId}/documents`), "Couldn't load the documents"),
  create: (companyId, fields) => call(
    () => apiClient.post(`/api/v2/companies/${companyId}/documents`, fields), "Couldn't add the document"),
  update: (id, fields) => call(
    () => apiClient.patch(`/api/v2/documents/${id}`, fields), "Couldn't save the document"),
  remove: (id) => call(
    () => apiClient.delete(`/api/v2/documents/${id}`), "Couldn't delete the document"),
  /** Pending suggestions: { company_id, suggestions: [...] } */
  suggestions: (companyId) => call(
    () => apiClient.get(`/api/v2/companies/${companyId}/document-suggestions`), "Couldn't load the suggestions"),
  /** Confirm with the fields the person edited (kind, status, title, dates); a 409 says the record has moved on */
  confirmSuggestion: (id, fields = {}) => call(
    () => apiClient.post(`/api/v2/document-suggestions/${id}/confirm`, fields), "Couldn't confirm the suggestion"),
  dismissSuggestion: (id) => call(
    () => apiClient.post(`/api/v2/document-suggestions/${id}/dismiss`), "Couldn't dismiss the suggestion"),
  /** Admins: { configured, linked, proposed, no_company } folders of the legal shared drive */
  legalFolders: () => call(() => apiClient.get('/api/v2/admin/legal-folders'), "Couldn't load the folders"),
  /** Admins: link a folder to a company, or unlink it with a null company id */
  linkLegalFolder: (folderId, companyId) => call(
    () => apiClient.put(`/api/v2/admin/legal-folders/${encodeURIComponent(folderId)}`, { company_id: companyId }),
    "Couldn't link the folder"),
  // { kind, status, expired, older_than_days, limit, offset }: empty values are left out
  list: (filters = {}) => call(
    () => apiClient.get('/api/v2/documents', {
      params: Object.fromEntries(Object.entries(filters).filter(([, value]) => value !== '' && value != null))
    }), "Couldn't load the documents")
};

export default documentsApi;
